import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  message,
  Modal,
  Form,
  Input,
  InputNumber,
  Image,
  Tooltip,
  Popconfirm,
  Select,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  SyncOutlined,
  ExportOutlined,
  ShopOutlined,
  DollarOutlined,
  DeleteOutlined,
  ReloadOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  getListings,
  updateListing,
  delistListing,
  relistListing,
  retryListing,
  deleteListing,
  getListingStatistics,
} from '../../api';
import { useSupplier } from '../../context/SupplierContext';
import type { ListingStatistics } from '../../api';
import { formatPrice, calculateProfitMargin } from '../../utils/price';
import type { ProductListing, PageInfo } from '../../types';

const { Title, Paragraph, Text } = Typography;

const statusConfig: Record<string, { text: string; color: string; bgColor: string }> = {
  draft: { text: '待上架', color: '#a0b1c5', bgColor: 'rgba(160, 177, 197, 0.1)' },
  listed: { text: '已上架', color: '#52c41a', bgColor: 'rgba(82, 196, 26, 0.1)' },
  delisted: { text: '已下架', color: '#faad14', bgColor: 'rgba(250, 173, 20, 0.1)' },
  error: { text: '上架失败', color: '#ff4d4f', bgColor: 'rgba(255, 77, 79, 0.1)' },
};

const Listings: React.FC = () => {
  const navigate = useNavigate();
  const { supplier } = useSupplier();
  const [loading, setLoading] = useState(false);
  const [listings, setListings] = useState<ProductListing[]>([]);
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    page: 1,
    pageSize: 100,
    totalNum: 0,
    totalPage: 0,
  });
  const [filterState, setFilterState] = useState<string | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [stats, setStats] = useState<ListingStatistics>({
    total: 0, listed: 0, draft: 0, delisted: 0, error: 0, totalRevenue: 0,
  });

  // 编辑弹窗
  const [editVisible, setEditVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [currentListing, setCurrentListing] = useState<ProductListing | null>(null);
  const [form] = Form.useForm();
  const listingRequestSeqRef = useRef(0);
  const statsRequestSeqRef = useRef(0);

  useEffect(() => {
    setListings([]);
    setSelectedRowKeys([]);
    setCurrentListing(null);
    setPageInfo((prev) => ({ ...prev, page: 1, totalNum: 0, totalPage: 0 }));
    setStats({ total: 0, listed: 0, draft: 0, delisted: 0, error: 0, totalRevenue: 0 });
  }, [supplier]);

  // 加载统计数据
  const loadStats = useCallback(async () => {
    const requestSeq = statsRequestSeqRef.current + 1;
    statsRequestSeqRef.current = requestSeq;
    try {
      const res = await getListingStatistics(supplier);
      if (res.success && res.data) {
        if (statsRequestSeqRef.current !== requestSeq) return;
        setStats(res.data);
      }
    } catch {
      // 统计加载失败不影响主流程
    }
  }, [supplier]);

  // 加载列表
  const loadListings = useCallback(async () => {
    const requestSeq = listingRequestSeqRef.current + 1;
    listingRequestSeqRef.current = requestSeq;
    setLoading(true);
    try {
      const res = await getListings({
        page: pageInfo.page,
        pageSize: pageInfo.pageSize,
        state: filterState,
      }, supplier);

      if (res.success && res.data) {
        if (listingRequestSeqRef.current !== requestSeq) return;
        setListings(res.data.records);
        setPageInfo(res.data.pageInfo);
      } else {
        if (listingRequestSeqRef.current !== requestSeq) return;
        setListings([]);
        setPageInfo((prev) => ({ ...prev, totalNum: 0, totalPage: 0 }));
        message.error(res.error || '获取列表失败');
      }
    } catch {
      if (listingRequestSeqRef.current !== requestSeq) return;
      setListings([]);
      setPageInfo((prev) => ({ ...prev, totalNum: 0, totalPage: 0 }));
      message.error('加载失败，请重试');
    } finally {
      if (listingRequestSeqRef.current === requestSeq) {
        setLoading(false);
      }
    }
  }, [pageInfo.page, pageInfo.pageSize, filterState, supplier]);

  useEffect(() => {
    loadListings();
    loadStats();
  }, [loadListings, loadStats]);

  // 打开编辑弹窗
  const handleEdit = (record: ProductListing) => {
    setCurrentListing(record);
    form.setFieldsValue({
      custom_title: record.custom_title || record.giga_product_name,
      selling_price: record.selling_price,
      custom_description: record.custom_description,
      shopify_product_type: record.shopify_product_type || record.giga_category,
    });
    setEditVisible(true);
  };

  // 保存编辑
  const handleSaveEdit = async (values: Record<string, unknown>) => {
    if (!currentListing) return;

    setEditLoading(true);
    try {
      const res = await updateListing(currentListing.id, {
        custom_title: values.custom_title as string,
        selling_price: values.selling_price as number,
        custom_description: values.custom_description as string,
        shopify_product_type: values.shopify_product_type as string,
      }, supplier);

      if (res.success) {
        message.success('保存成功');
        setEditVisible(false);
        loadListings();
        loadStats();
      } else {
        message.error(res.error || '保存失败');
      }
    } catch {
      message.error('保存失败，请重试');
    } finally {
      setEditLoading(false);
    }
  };

  // 重新同步到 Odoo 产品（Ventor 会自动推送到 Shopify）
  const handleResyncToOdoo = async (record: ProductListing) => {
    try {
      message.loading({ content: '正在同步到 Odoo 产品...', key: 'odoo-sync', duration: 0 });
      const res = await updateListing(record.id, {
        resync: true,
      }, supplier);
      if (res.success) {
        message.success({
          content: 'Odoo 产品同步成功！Ventor 将自动推送到 Shopify',
          key: 'odoo-sync',
        });
        loadListings();
        loadStats();
      } else {
        message.error({ content: res.error || '同步失败', key: 'odoo-sync' });
      }
    } catch {
      message.error({ content: '操作失败，请重试', key: 'odoo-sync' });
    }
  };

  // 下架（归档 Odoo 产品 → Ventor 同步 Shopify draft）
  const handleDelist = async (record: ProductListing) => {
    try {
      message.loading({ content: '正在下架...', key: 'delist', duration: 0 });
      const res = await delistListing(record.id, supplier);
      if (res.success) {
        message.success({ content: '已下架，Ventor 将自动在 Shopify 设为 draft', key: 'delist' });
        loadListings();
        loadStats();
      } else {
        message.error({ content: res.error || '下架失败', key: 'delist' });
      }
    } catch {
      message.error({ content: '下架失败，请重试', key: 'delist' });
    }
  };

  // 重新上架（取消归档 Odoo 产品 → Ventor 同步 Shopify active）
  const handleRelist = async (record: ProductListing) => {
    try {
      message.loading({ content: '正在重新上架...', key: 'relist', duration: 0 });
      const res = await relistListing(record.id, supplier);
      if (res.success) {
        message.success({ content: '已重新上架，Ventor 将自动恢复 Shopify active', key: 'relist' });
        loadListings();
        loadStats();
      } else {
        message.error({ content: res.error || '重新上架失败', key: 'relist' });
      }
    } catch {
      message.error({ content: '重新上架失败，请重试', key: 'relist' });
    }
  };

  // 重试
  const handleRetry = async (record: ProductListing) => {
    try {
      message.loading({ content: '正在重试...', key: 'retry', duration: 0 });
      const res = await retryListing(record.id, supplier);
      if (res.success) {
        message.success({ content: '重试成功', key: 'retry' });
        loadListings();
        loadStats();
      } else {
        message.error({ content: res.error || '重试失败', key: 'retry' });
      }
    } catch {
      message.error({ content: '重试失败，请重试', key: 'retry' });
    }
  };

  // 删除
  const handleDelete = async (record: ProductListing) => {
    try {
      message.loading({ content: '正在删除...', key: 'delete', duration: 0 });
      const res = await deleteListing(record.id, supplier);
      if (res.success) {
        message.success({ content: '删除成功', key: 'delete' });
        setSelectedRowKeys((prev) => prev.filter((k) => k !== record.id));
        loadListings();
        loadStats();
      } else {
        message.error({ content: res.error || '删除失败', key: 'delete' });
      }
    } catch {
      message.error({ content: '删除失败，请重试', key: 'delete' });
    }
  };


  const columns: ColumnsType<ProductListing> = [
    {
      title: '产品图片',
      dataIndex: 'giga_main_image',
      key: 'giga_main_image',
      width: 80,
      render: (url: string) => (
        <div style={{ 
          background: 'rgba(0,0,0,0.2)', 
          padding: 4, 
          borderRadius: 8, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Image
            src={url || 'https://via.placeholder.com/50x50?text=No+Image'}
            width={50}
            height={50}
            style={{ objectFit: 'contain' }}
            fallback="https://via.placeholder.com/50x50?text=Error"
          />
        </div>
      ),
    },
    {
      title: '产品信息',
      key: 'product',
      width: 250,
      render: (_, record) => (
        <div>
          <Tooltip title={record.custom_title || record.giga_product_name}>
            <Text strong style={{ width: 230, display: 'block', color: '#fff' }}>
              {record.custom_title || record.giga_product_name}
            </Text>
          </Tooltip>
            <Text style={{ fontSize: 12, color: '#a0b1c5' }}>
            SKU: {record.giga_sku}
          </Text>
          {record.shopify_product_type && (
            <div style={{ marginTop: 4 }}>
              <Tag style={{ 
                fontSize: 10, 
                background: 'rgba(79, 110, 247, 0.1)', 
                color: '#4f6ef7', 
                border: '1px solid rgba(79, 110, 247, 0.2)' 
              }}>{record.shopify_product_type}</Tag>
            </div>
          )}
          {record.giga_description_missing && (
            <div style={{ marginTop: 4 }}>
              <Tooltip title={record.giga_characteristics_count ? 'GIGA 图文描述为空，已使用产品特点作为描述' : 'GIGA 图文描述和产品特点均为空'}>
                <Tag color={record.giga_characteristics_count ? 'gold' : 'red'} style={{ fontSize: 10 }}>
                  图文描述缺失
                </Tag>
              </Tooltip>
            </div>
          )}
        </div>
      ),
    },
    {
      title: '分销价',
      dataIndex: 'giga_price',
      key: 'giga_price',
      width: 100,
      render: (price: number) => (price ? <Text style={{ color: '#a0b1c5' }}>{formatPrice(price)}</Text> : '-'),
    },
    {
      title: '销售价',
      dataIndex: 'selling_price',
      key: 'selling_price',
      width: 100,
      render: (price: number) => (
        <Text strong style={{ color: '#00d4ff', textShadow: '0 0 10px rgba(0, 212, 255, 0.3)' }}>
          {formatPrice(price)}
        </Text>
      ),
    },
    {
      title: '利润率',
      key: 'profit',
      width: 80,
      render: (_, record) => {
        const margin = calculateProfitMargin(record.selling_price, record.giga_price || 0, record.giga_shipping_fee || 0);
        return (
          <Text style={{ color: margin >= 20 ? '#52c41a' : margin >= 10 ? '#faad14' : '#ff4d4f' }}>
            {margin.toFixed(1)}%
          </Text>
        );
      },
    },
    {
      title: '库存',
      dataIndex: 'giga_available_qty',
      key: 'giga_available_qty',
      width: 80,
      filters: [
        { text: '有库存', value: 'inStock' },
        { text: '无库存', value: 'outOfStock' },
      ],
      onFilter: (value, record) => {
        if (value === 'inStock') return (record.giga_available_qty || 0) > 0;
        if (value === 'outOfStock') return (record.giga_available_qty || 0) === 0;
        return true;
      },
      render: (qty: number) => (
        <Text style={{ color: qty > 10 ? '#52c41a' : qty > 0 ? '#faad14' : '#ff4d4f' }}>
          {qty ?? '-'}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'state',
      key: 'state',
      width: 100,
      render: (state: string, record) => {
        const config = statusConfig[state] || { text: state, color: '#a0b1c5', bgColor: 'rgba(255,255,255,0.05)' };
        return (
          <Tooltip title={record.error_message || undefined}>
            <Tag style={{ 
              color: config.color, 
              background: config.bgColor, 
              border: `1px solid ${config.color}33`,
              borderRadius: 4,
              padding: '0 8px'
            }}>
              {config.text}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Shopify ID',
      dataIndex: 'shopify_product_id',
      key: 'shopify_product_id',
      width: 120,
      render: (id: string) => id || <Text type="secondary">未关联</Text>,
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Tooltip title="编辑">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined style={{ color: '#00d4ff' }} />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="查看产品">
            <Button
              type="text"
              size="small"
              icon={<ShopOutlined style={{ color: '#00d4ff' }} />}
              onClick={() => navigate(`/products/${record.giga_sku}`)}
            />
          </Tooltip>

          {/* draft / error: 同步到 Shopify */}
          {(record.state === 'draft' || record.state === 'error') && (
            <Tooltip title="同步到 Shopify（通过 Odoo 产品）">
              <Button
                type="text"
                size="small"
                icon={<ExportOutlined style={{ color: '#4f6ef7' }} />}
                onClick={() => handleResyncToOdoo(record)}
              />
            </Tooltip>
          )}

          {/* error: 重试 */}
          {record.state === 'error' && (
            <Tooltip title="重试">
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined style={{ color: '#faad14' }} />}
                onClick={() => handleRetry(record)}
              />
            </Tooltip>
          )}

          {/* listed: 下架 */}
          {record.state === 'listed' && (
            <Popconfirm
              title="确定要下架此产品吗？"
              description="Ventor 将自动在 Shopify 设为 draft（不可见但保留数据）"
              onConfirm={() => handleDelist(record)}
            >
              <Tooltip title="下架">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<PauseCircleOutlined />}
                />
              </Tooltip>
            </Popconfirm>
          )}

          {/* delisted: 重新上架 */}
          {record.state === 'delisted' && (
            <Tooltip title="重新上架">
              <Button
                type="text"
                size="small"
                icon={<PlayCircleOutlined />}
                style={{ color: '#52c41a' }}
                onClick={() => handleRelist(record)}
              />
            </Tooltip>
          )}

          {/* 所有状态: 删除 */}
          <Popconfirm
            title="确定要删除此记录吗？"
            description="将同时归档关联的 Odoo 产品"
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title="删除">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>上架管理</Title>
        <Paragraph type="secondary">
          管理已添加的上架产品，设置价格并推送到 Shopify。下架/上架通过 Odoo 产品归档/取消归档实现，Ventor 自动同步。
        </Paragraph>
      </div>

      {/* 统计卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={5}>
          <Card size="small">
            <Statistic title="总产品数" value={stats.total} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="已上架" value={stats.listed} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="待上架" value={stats.draft} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
        <Col span={5}>
          <Card size="small">
            <Statistic title="已下架" value={stats.delisted} valueStyle={{ color: '#ff7a45' }} />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small">
            <Statistic
              title="总售价"
              value={stats.totalRevenue}
              precision={2}
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* 操作栏 */}
      <Card>
        <Space style={{ marginBottom: 16 }} wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/products')}
          >
            添加产品
          </Button>
          <Select
            placeholder="筛选状态"
            allowClear
            style={{ width: 150 }}
            value={filterState}
            onChange={(value) => {
              setFilterState(value);
              setPageInfo((prev) => ({ ...prev, page: 1 }));
            }}
          >
            <Select.Option value="draft">待上架</Select.Option>
            <Select.Option value="listed">已上架</Select.Option>
            <Select.Option value="delisted">已下架</Select.Option>
            <Select.Option value="error">上架失败</Select.Option>
          </Select>
          <Button icon={<SyncOutlined />} onClick={loadListings}>
            刷新
          </Button>
          {selectedRowKeys.length > 0 && (
            <Text type="secondary">已选择 {selectedRowKeys.length} 项</Text>
          )}
        </Space>

        <Table
          columns={columns}
          dataSource={listings}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1300 }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          pagination={{
            current: pageInfo.page,
            pageSize: pageInfo.pageSize,
            total: pageInfo.totalNum,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => {
              setPageInfo((prev) => ({ ...prev, page, pageSize }));
            },
          }}
        />
      </Card>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑上架信息"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        footer={null}
        width={500}
      >
        {currentListing && (
          <Form form={form} layout="vertical" onFinish={handleSaveEdit}>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <Image
                src={currentListing.giga_main_image || 'https://via.placeholder.com/100x100'}
                width={100}
                height={100}
                style={{ objectFit: 'cover' }}
              />
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">SKU: {currentListing.giga_sku}</Text>
              </div>
            </div>

            <Form.Item
              name="custom_title"
              label="产品标题"
              rules={[{ required: true, message: '请输入产品标题' }]}
            >
              <Input placeholder="自定义在 Shopify 显示的标题" />
            </Form.Item>

            <Form.Item
              name="selling_price"
              label="销售价格 (USD)"
              rules={[{ required: true, message: '请输入销售价格' }]}
              extra={
                <Text type="secondary">
                  成本价: {formatPrice(currentListing.giga_price || 0)}
                </Text>
              }
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: '100%' }}
                prefix="$"
              />
            </Form.Item>

            <Form.Item
              name="shopify_product_type"
              label="Shopify 类别"
              extra="同步到 Shopify 的 product_type 字段"
            >
              <Input placeholder="如：Furniture, Electronics, Toys..." />
            </Form.Item>

            <Form.Item name="custom_description" label="产品描述">
              <Input.TextArea rows={4} placeholder="自定义产品描述（可选）" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={editLoading}>
                  保存
                </Button>
                <Button onClick={() => setEditVisible(false)}>取消</Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default Listings;
